/**
 * Help & Guide — a single, searchable in-app user guide for TekInvest
 * pilot users. Reuses this app's existing layout (MainLayout/TopNav),
 * MUI components, and theme tokens — no new UI framework, no blocking
 * modals or onboarding tour, just a normal page.
 *
 * Deep-linkable: `/help?section=<id>` (see `helpContent.ts` for the id
 * list) opens directly on that category — used by the contextual
 * `HelpGuideLink` icon buttons on Broker Account, Algo Strategies, and
 * Option Analyst. The `section` query param stays in sync as the user
 * clicks around, so the current view is always shareable/bookmarkable.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { HELP_SECTIONS, RISK_DISCLAIMER, type HelpSection } from "@/pages/help/helpContent";

const DEFAULT_SECTION_ID = HELP_SECTIONS[0].id;

function sectionMatches(section: HelpSection, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  const haystack = [
    section.title,
    section.whatIsIt,
    section.whyUseIt,
    ...section.howToUse,
    ...section.importantPoints,
  ]
    .join(" \n ")
    .toLowerCase();
  return haystack.includes(normalizedQuery);
}

export function HelpGuidePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string>(() => {
    const fromUrl = searchParams.get("section");
    return fromUrl && HELP_SECTIONS.some((s) => s.id === fromUrl) ? fromUrl : DEFAULT_SECTION_ID;
  });

  // Keep the visible section in sync if the URL's ?section= changes from
  // outside this page (e.g. a contextual Help & Guide icon link is
  // clicked again while this page is already open).
  useEffect(() => {
    const fromUrl = searchParams.get("section");
    if (fromUrl && HELP_SECTIONS.some((s) => s.id === fromUrl) && fromUrl !== selectedId) {
      setSelectedId(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const normalizedQuery = query.trim().toLowerCase();
  const matchedSections = useMemo(
    () => HELP_SECTIONS.filter((section) => sectionMatches(section, normalizedQuery)),
    [normalizedQuery]
  );

  // If a search narrows the list past the currently-open section, jump to
  // the first remaining match rather than showing a selected-but-hidden
  // sidebar item next to unrelated content.
  useEffect(() => {
    if (matchedSections.length > 0 && !matchedSections.some((s) => s.id === selectedId)) {
      setSelectedId(matchedSections[0].id);
    }
  }, [matchedSections, selectedId]);

  function handleSelect(id: string) {
    setSelectedId(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("section", id);
        return next;
      },
      { replace: true }
    );
  }

  const selectedSection = HELP_SECTIONS.find((s) => s.id === selectedId) ?? HELP_SECTIONS[0];

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Help &amp; Guide
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={2}>
        Everything you need to get the most out of TekInvest — organized by topic, and searchable below.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">{RISK_DISCLAIMER}</Typography>
      </Alert>

      <TextField
        fullWidth
        placeholder='Search help topics (e.g. "deactivate", "broker", "backtest")'
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper variant="outlined" sx={{ p: 1, position: { md: "sticky" }, top: { md: 80 } }}>
            {matchedSections.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No help topics match "{query}". Try a different word, or browse the FAQ.
              </Typography>
            ) : (
              <List dense disablePadding>
                {matchedSections.map((section) => (
                  <ListItemButton
                    key={section.id}
                    selected={section.id === selectedId}
                    onClick={() => handleSelect(section.id)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={section.title}
                      slotProps={{ primary: { fontWeight: section.id === selectedId ? 700 : 500 } }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {selectedSection.title}
            </Typography>

            <Stack spacing={3} mt={2}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                  What is it
                </Typography>
                <Typography variant="body1">{selectedSection.whatIsIt}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                  Why use it
                </Typography>
                <Typography variant="body1">{selectedSection.whyUseIt}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                  How to use it
                </Typography>
                <Box component="ol" sx={{ pl: 3, m: 0 }}>
                  {selectedSection.howToUse.map((step, index) => (
                    <Typography component="li" variant="body1" key={index} sx={{ mb: 1 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ alignItems: "flex-start" }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Important points
                </Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {selectedSection.importantPoints.map((point, index) => (
                    <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default HelpGuidePage;
